"use client"

import { createContext, useContext } from "react"

const ApartContext = createContext()

export const useApartContext = () => {
  const context = useContext(ApartContext)
  if (!context) {
    throw new Error("useApartContext must be used within an ApartContextProvider")
  }
  return context
}

const ApartContextProvider = ({ children }) => {
  const apartmentData = {
    id: 4,
    titleEn: "✨J Tower Balcony✨",
    titleEs: "✨J Tower Balcony✨",
    titleFr: "✨J Tower Balcony✨",
    titleHe: "✨J Tower Balcony✨",
    internalCode: "(G8)",
    descriptionEn: "Elegant 1-Bedroom Apartment with Balcony – Prime Location Near Mahane Yehuda Market",
    descriptionEs: "Elegante apartamento de 1 dormitorio con balcón – Ubicación privilegiada cerca del Mercado Mahane Yehuda",
    descriptionFr: "Élégant appartement d'une chambre avec balcon – Emplacement privilégié près du marché Mahane Yehuda",
    descriptionHe: "דירת חדר שינה אחת אלגנטית עם מרפסת – מיקום מרכזי ליד שוק מחנה יהודה",
    longDescriptionEn: "Enjoy the perfect combination of style, comfort, and location in this fully equipped 1-bedroom apartment with a spacious living room and private balcony. Located just a minute's walk from Mahane Yehuda Market, this apartment is ideal for travelers seeking a central and relaxing base in the heart of Jerusalem.\n\nThe balcony offers a quiet outdoor spot to enjoy your morning coffee or unwind after a day of exploring the city.",
    longDescriptionEs: "Disfrute de la combinación perfecta de estilo, comodidad y ubicación en este apartamento de 1 dormitorio totalmente equipado con una espaciosa sala de estar y balcón privado...",
    longDescriptionFr: "Profitez de la combinaison parfaite de style, de confort et d'emplacement dans cet appartement d'une chambre entièrement équipé...",
    longDescriptionHe: "תיהנו מהשילוב המושלם של סגנון, נוחות ומיקום בדירת חדר שינה אחת מאובזרת לחלוטין עם סלון מרווח ומרפסת פרטית. הדירה ממוקמת דקת הליכה משוק מחנה יהודה, ומושלמת למטיילים שמחפשים נקודת מוצא מרכזית ונעימה בלב ירושלים.\n\nהמרפסת מהווה פינה שקטה בחוץ ליהנות מקפה של בוקר או להירגע אחרי יום של חוויות בעיר.",
    size: 50,
    guests: 2,
    bedrooms: 1,
    bathrooms: 1,
    images: [
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569724/Apart_4_3_ng0bmv.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569722/Apart_4_1_l5zzfb.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569723/Apart_4_2_apvyqy.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569725/Apart_4_4_zm0gef.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569726/Apart_4_5_vvujfu.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569727/Apart_4_6_aicg47.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569728/Apart_4_7_s9e507.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569729/Apart_4_8_kuozq5.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569730/Apart_4_9_txr06a.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569731/Apart_4_10_d20ssi.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569733/Apart_4_11_tgzk1s.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569733/Apart_4_12_m5yiq4.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569734/Apart_4_13_wynpdu.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569736/Apart_4_14_f8vn5m.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569737/Apart_4_15_hmuvnp.png",
      "https://res.cloudinary.com/dp3vi9xno/image/upload/v1752569738/Apart_4_16_j5b43w.png"
    ],
    amenities: [
      { name: "Fully Equipped Kitchen", nameEs: "Cocina Totalmente Equipada", nameFr: "Cuisine Entièrement Équipée", nameHe: "מטבח מאובזר", icon: "kitchen" },
      { name: "Air Conditioning", nameEs: "Aire Acondicionado", nameFr: "Climatisation", nameHe: "מיזוג אוויר", icon: "air" },
      { name: "Smart TV", nameEs: "Smart TV", nameFr: "Smart TV", nameHe: "טלוויזיה חכמה", icon: "tv" },
      { name: "High-Speed Wi-Fi", nameEs: "Wi-Fi de Alta Velocidad", nameFr: "Wi-Fi Haut Débit", nameHe: "אינטרנט אלחוטי מהיר", icon: "wifi" },
      { name: "Washing Machine", nameEs: "Lavadora", nameFr: "Machine à Laver", nameHe: "מכונת כביסה", icon: "washer" },
      { name: "Nespresso Machine", nameEs: "Máquina Nespresso", nameFr: "Machine Nespresso", nameHe: "מכונת נספרסו", icon: "coffee" },
      { name: "Fresh Linens & Towels", nameEs: "Sábanas y Toallas Limpias", nameFr: "Draps et Serviettes Frais", nameHe: "מצעים ומגבות נקיים", icon: "bed" },
      { name: "Private Balcony", nameEs: "Balcón Privado", nameFr: "Balcon Privé", nameHe: "מרפסת פרטית", icon: "balcony" },
      { name: "Dining Area", nameEs: "Área de Comedor", nameFr: "Coin Repas", nameHe: "פינת אוכל", icon: "dining" },
      { name: "Refrigerator", nameEs: "Refrigerador", nameFr: "Réfrigérateur", nameHe: "מקרר", icon: "fridge" },
      { name: "Microwave", nameEs: "Microondas", nameFr: "Micro-ondes", nameHe: "מיקרוגל", icon: "microwave" },
      { name: "Hairdryer", nameEs: "Secador de Pelo", nameFr: "Sèche-cheveux", nameHe: "מייבש שיער", icon: "hairdryer" },
      { name: "Iron & Ironing Board", nameEs: "Plancha y Tabla de Planchar", nameFr: "Fer et Planche à Repasser", nameHe: "מגהץ וקרש גיהוץ", icon: "iron" },
      { name: "Cleaning Supplies", nameEs: "Artículos de Limpieza", nameFr: "Produits de Nettoyage", nameHe: "חומרי ניקוי", icon: "cleaning" }
    ],
    location: {
      addressEn: "12 Agrippas Street, Jerusalem",
      addressEs: "12 Calle Agrippas, Jerusalén",
      addressFr: "12 Rue Agrippas, Jérusalem",
      addressHe: "רחוב אגריפס 12, ירושלים",
      addressDescEn: "Near Mahane Yehuda Market, Jerusalem",
      addressDescEs: "Cerca del Mercado Mahane Yehuda, Jerusalén",
      addressDescFr: "Près du Marché Mahane Yehuda, Jérusalem",
      addressDescHe: "ליד שוק מחנה יהודה, ירושלים",
      coordinates: {
        lat: 31.7849,
        lng: 35.2120
      },
      highlights: [
        { nameEn: "1-minute walk to Mahane Yehuda Market", nameEs: "A 1 minuto a pie del Mercado Mahane Yehuda", nameFr: "À 1 minute à pied du Marché Mahane Yehuda", nameHe: "דקת הליכה לשוק מחנה יהודה" },
        { nameEn: "5-minute walk to light rail station", nameEs: "A 5 minutos a pie de la estación de tren ligero", nameFr: "À 5 minutes à pied de la station de tramway", nameHe: "5 דקות הליכה מהרכבת הקלה" },
        { nameEn: "Surrounded by cafes, restaurants and bars", nameEs: "Rodeado de cafés, restaurantes y bares", nameFr: "Entouré de cafés, restaurants et bars", nameHe: "מוקף בבתי קפה, מסעדות וברים" },
        { nameEn: "15-minute walk to the Old City", nameEs: "A 15 minutos a pie de la Ciudad Vieja", nameFr: "À 15 minutes à pied de la Vieille Ville", nameHe: "15 דקות הליכה לעיר העתיקה" },
        { nameEn: "Close to public transportation", nameEs: "Cerca del transporte público", nameFr: "Proche des transports en commun", nameHe: "גישה נוחה לתחבורה ציבורית" },
        { nameEn: "Many grocery stores and supermarkets nearby", nameEs: "Muchas tiendas de comestibles y supermercados cercanos", nameFr: "Nombreuses épiceries et supermarchés à proximité", nameHe: "שפע חנויות מכולת וסופרים בקרבת מקום" }
      ]
    }
  }

  return (
    <ApartContext.Provider value={{ apartmentData }}>
      {children}
    </ApartContext.Provider>
  )
}

export default ApartContextProvider
